import React, { useEffect, useState } from "react";
import { ArrowDown, ArrowRight, CheckCircle2, ChevronLeft, ChevronRight, FileJson, GripVertical, ImageIcon, Plus, RotateCcw, WandSparkles, X } from "lucide-react";

// The Workflow tab: the customer arranges how the components work together.
// Drag steps to reorder them, drag components in from the list (or press +), and
// remove the ones they don't need. The flow is a picture of the working order —
// each component is still used from the Components tab. Only the order is
// remembered, in this browser.
//
// How steps pass results on: a step receives the result of every step before it
// that it can use (Image Creation takes an image and/or a menu item).

const KINDS = { image: "image", item: "menu item" };

const STEPS = {
  "image-extraction": {
    name: "Image Extraction",
    icon: ImageIcon,
    tone: "bg-indigo-50 text-indigo-600",
    text: "Takes the item name and finds that product's picture in your PDF, then puts it on a clean white background.",
    gives: "image",
    accepts: ["item"],
  },
  "menu-data-extraction": {
    name: "Menu Data Extraction",
    icon: FileJson,
    tone: "bg-amber-50 text-amber-600",
    text: "Reads a menu photo or PDF and lists its items with name, price and description.",
    gives: "item",
    accepts: [],
  },
  "image-creation": {
    name: "Image Creation",
    icon: WandSparkles,
    tone: "bg-indigo-50 text-indigo-600",
    text: "Puts the extracted product image into your template with AI and adds the item's name, price and description.",
    gives: "image",
    accepts: ["image", "item"],
    extra: "Template image (you upload the design to reuse)",
  },
};

const DEFAULT_ORDER = ["menu-data-extraction", "image-extraction", "image-creation"];
const STORAGE_KEY = "workflowFlowOrder.v2";
const DRAG_TYPE = "application/x-flow-step";

const loadOrder = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY));
    if (Array.isArray(saved) && saved.every((k) => STEPS[k]) && new Set(saved).size === saved.length) return saved;
  } catch {
    /* storage unavailable or corrupt: use the default */
  }
  return DEFAULT_ORDER;
};

// What a step receives: results of earlier steps that it can use.
const receivesFor = (order, index) => {
  const accepts = STEPS[order[index]].accepts;
  return order
    .slice(0, index)
    .filter((k) => accepts.includes(STEPS[k].gives))
    .map((k) => `${KINDS[STEPS[k].gives]} from ${STEPS[k].name}`);
};

const PILL = {
  ok: "bg-green-50 text-green-700 border-green-200",
  warn: "bg-amber-50 text-amber-700 border-amber-200",
  idle: "bg-gray-50 text-gray-600 border-gray-200",
};

// `renderStep(key)` renders the real component for a step, `status[key]` = { tone, text } is its live state,
// and `finalResult` is the card shown after the last step.
export default function WorkflowFlow({ renderStep, status = {}, finalResult }) {
  const [order, setOrder] = useState(loadOrder);
  const [dragKey, setDragKey] = useState(null);
  const [overIndex, setOverIndex] = useState(null); // insertion slot being hovered

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(order));
    } catch {
      /* ignore */
    }
  }, [order]);

  // Put `key` at insertion slot `slot` (0 = before the first step).
  const place = (key, slot) => {
    setOrder((prev) => {
      const from = prev.indexOf(key);
      const next = prev.filter((k) => k !== key);
      const at = from !== -1 && from < slot ? slot - 1 : slot;
      next.splice(at, 0, key);
      return next;
    });
  };
  const move = (key, delta) => {
    setOrder((prev) => {
      const i = prev.indexOf(key);
      const j = i + delta;
      if (j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  };
  const remove = (key) => setOrder((prev) => prev.filter((k) => k !== key));
  const endDrag = () => {
    setDragKey(null);
    setOverIndex(null);
  };

  const slotProps = (slot) => ({
    onDragOver: (e) => {
      if (!e.dataTransfer.types.includes(DRAG_TYPE)) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      if (overIndex !== slot) setOverIndex(slot);
    },
    onDragLeave: () => setOverIndex((cur) => (cur === slot ? null : cur)),
    onDrop: (e) => {
      const key = e.dataTransfer.getData(DRAG_TYPE);
      if (!STEPS[key]) return;
      e.preventDefault();
      place(key, slot);
      endDrag();
    },
  });

  const available = Object.keys(STEPS).filter((k) => !order.includes(k));
  const isDefault = order.join() === DEFAULT_ORDER.join();

  // The arrow between two steps (also the place to drop a step).
  const Slot = ({ slot, label, tone }) => (
    <div
      {...slotProps(slot)}
      className={`flex flex-none items-center justify-center rounded-xl text-xs font-semibold transition-colors md:min-w-[84px] md:flex-col ${
        dragKey ? "min-h-[44px] border-2 border-dashed md:min-h-[120px]" : "py-1"
      } ${overIndex === slot ? "border-indigo-500 bg-indigo-50 text-indigo-700" : dragKey ? "border-gray-300 text-gray-400" : tone || "text-gray-400"}`}
      data-slot={slot}
    >
      {dragKey ? (
        <span>{overIndex === slot ? "Drop here" : "Drop"}</span>
      ) : (
        <>
          <ArrowDown size={18} className="md:hidden" />
          <ArrowRight size={18} className="hidden md:block" />
          {label && <span className="ml-1.5 md:ml-0 md:mt-0.5">{label}</span>}
        </>
      )}
    </div>
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3 rounded-2xl border border-indigo-100 bg-indigo-50/60 p-4 text-sm text-gray-700">
        <div className="min-w-0 flex-1">
          <p className="m-0 font-semibold text-gray-900">Arrange your workflow</p>
          <p className="m-0 mt-1">
            Drag the steps to change the order, add the ones you need and remove the rest. Each step passes its result to the steps after it.
            Use each component from the <b>Components</b> tab.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setOrder(DEFAULT_ORDER)}
          disabled={isDefault}
          className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:border-gray-400 disabled:opacity-50"
        >
          <RotateCcw size={13} /> Reset
        </button>
      </div>

      {/* ---------- Components not in the flow ---------- */}
      <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-gray-200 bg-white p-3">
        <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">Add a step</span>
        {available.length === 0 ? (
          <span className="text-sm text-gray-500">All components are in the flow.</span>
        ) : (
          available.map((key) => {
            const step = STEPS[key];
            const Icon = step.icon;
            return (
              <div
                key={key}
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.setData(DRAG_TYPE, key);
                  e.dataTransfer.effectAllowed = "move";
                  setDragKey(key);
                }}
                onDragEnd={endDrag}
                className="inline-flex cursor-grab items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 py-1.5 pl-2 pr-1.5 active:cursor-grabbing"
              >
                <span className={`grid h-7 w-7 place-items-center rounded-lg ${step.tone}`}>
                  <Icon size={15} />
                </span>
                <span className="text-sm font-medium text-gray-900">{step.name}</span>
                <button
                  type="button"
                  onClick={() => setOrder((prev) => [...prev, key])}
                  aria-label={`Add ${step.name}`}
                  className="grid h-7 w-7 place-items-center rounded-lg text-gray-600 hover:bg-white hover:text-indigo-700"
                >
                  <Plus size={15} />
                </button>
              </div>
            );
          })
        )}
      </div>

      {/* ---------- The flow ---------- */}
      <div className="flex flex-col items-stretch gap-1 md:flex-row md:items-start md:gap-2" data-testid="flow">
        {dragKey && <Slot slot={0} />}
        {order.map((key, i) => {
          const step = STEPS[key];
          const Icon = step.icon;
          const receives = receivesFor(order, i);
          const isLast = i === order.length - 1;
          const unused = !isLast && !order.slice(i + 1).some((k) => STEPS[k].accepts.includes(step.gives));
          const linkLabel = isLast ? "result" : KINDS[step.gives];
          return (
            <React.Fragment key={key}>
              <article
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.setData(DRAG_TYPE, key);
                  e.dataTransfer.effectAllowed = "move";
                  setDragKey(key);
                }}
                onDragEnd={endDrag}
                data-step={key}
                className={`flex flex-1 cursor-grab flex-col gap-1.5 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm active:cursor-grabbing md:min-w-0 ${dragKey === key ? "opacity-40" : ""}`}
              >
                <div className="flex items-center gap-1">
                  <GripVertical size={16} className="flex-none text-gray-300" />
                  <span className="flex-1 text-xs font-semibold uppercase tracking-wide text-gray-400">Step {i + 1}</span>
                  <button type="button" onClick={() => move(key, -1)} disabled={i === 0} aria-label={`Move ${step.name} earlier`} className="rounded p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30">
                    <ChevronLeft size={15} />
                  </button>
                  <button type="button" onClick={() => move(key, 1)} disabled={i === order.length - 1} aria-label={`Move ${step.name} later`} className="rounded p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30">
                    <ChevronRight size={15} />
                  </button>
                  <button type="button" onClick={() => remove(key)} aria-label={`Remove ${step.name}`} className="rounded p-1 text-gray-400 hover:text-red-600">
                    <X size={15} />
                  </button>
                </div>
                <div className="flex items-center gap-2.5">
                  <span className={`grid h-9 w-9 flex-none place-items-center rounded-lg ${step.tone}`}>
                    <Icon size={18} />
                  </span>
                  <h3 className="m-0 min-w-0 flex-1 text-sm font-semibold text-gray-900">{step.name}</h3>
                </div>
                <p className="m-0 text-sm text-gray-600">{step.text}</p>
                <p className="m-0 text-xs text-gray-500">
                  <b className="text-gray-700">Gives:</b> {KINDS[step.gives]}
                </p>
                <p className="m-0 text-xs text-gray-500">
                  <b className="text-gray-700">Receives:</b>{" "}
                  {receives.length ? receives.join(" + ") : step.accepts.length ? "nothing before it — you add the image yourself" : "nothing, it starts from your file"}
                </p>
                {step.extra && (
                  <p className="m-0 text-xs text-gray-500">
                    <b className="text-gray-700">You add:</b> {step.extra}
                  </p>
                )}
                {unused && <p className="m-0 text-xs font-medium text-amber-600">No later step uses this result.</p>}
              </article>
              <Slot slot={i + 1} label={linkLabel} tone={isLast ? "text-green-600" : "text-indigo-600"} />
            </React.Fragment>
          );
        })}

        {order.length === 0 && (
          <div className="flex-1 rounded-2xl border-2 border-dashed border-gray-200 px-4 py-8 text-center text-sm text-gray-500" {...slotProps(0)}>
            Your workflow is empty. Add a step above.
          </div>
        )}

        {order.length > 0 && (
          <div className="flex flex-1 flex-col gap-1.5 rounded-2xl border border-green-200 bg-green-50/50 p-4 md:min-w-0" aria-label="Final result">
            <div className="flex items-center gap-2.5">
              <span className="grid h-9 w-9 flex-none place-items-center rounded-lg bg-green-100 text-green-600">
                <CheckCircle2 size={18} />
              </span>
              <h3 className="m-0 text-sm font-semibold text-gray-900">Final result</h3>
            </div>
            <p className="m-0 text-sm text-gray-600">The last step's result — preview it and download it as a PNG.</p>
          </div>
        )}
      </div>

      {/* ---------- Run the workflow: the real components, in flow order ---------- */}
      {renderStep && order.length > 0 && (
        <div className="flex flex-col gap-4" data-testid="flow-run">
          <div>
            <h3 className="m-0 text-base font-bold text-gray-900">Run your workflow</h3>
            <p className="m-0 mt-1 text-sm text-gray-600">
              Work through the steps in order. Each step's real result is passed on to the next one automatically.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
